import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Input, Select, message, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import PageHeader from '../../components/PageHeader';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import OrganizationListCard from '../../components/OrganizationListCard';
import type { OrganizationListCardHandlers } from '../../components/OrganizationListCard';
import {
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  Organization,
  CreateOrganizationData,
} from '../../services/adminService';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import ResponsiveModal from '../../components/ResponsiveModal';

const OrganizationList: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Sort: platform first, then alphabetical
  const sortOrgs = (orgs: Organization[]) => {
    return [...orgs].sort((a, b) => {
      if (a.type === 'platform' && b.type !== 'platform') return -1;
      if (a.type !== 'platform' && b.type === 'platform') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await getOrganizations();
      setOrganizations(sortOrgs(data));
    } catch (err: any) {
      message.error(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleCreate = async (values: CreateOrganizationData) => {
    setSubmitting(true);
    try {
      await createOrganization(values);
      message.success('Organization created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchOrganizations();
    } catch (err: any) {
      message.error(err.message || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: Organization) => {
    setEditingOrg(record);
    editForm.setFieldsValue({
      name: record.name,
      type: record.type,
      parent_org_id: record.parent_org_id || undefined,
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingOrg) return;
    setSubmitting(true);
    try {
      await updateOrganization(editingOrg.id, {
        name: values.name,
        ...(editingOrg.type !== 'platform' && values.type ? { type: values.type } : {}),
        ...(editingOrg.type !== 'platform' ? { parent_org_id: values.parent_org_id } : {}),
      } as any);
      message.success('Organization updated successfully');
      setEditModalVisible(false);
      setEditingOrg(null);
      editForm.resetFields();
      fetchOrganizations();
    } catch (err: any) {
      message.error(err.message || 'Failed to update organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteOrganization(id);
      message.success('Organization deleted successfully');
      fetchOrganizations();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete organization');
    }
  };

  const cardHandlers: OrganizationListCardHandlers = {
    onEdit: handleEdit,
    onDelete: handleDelete,
  };

  const typeColor = (type: string) => {
    switch (type) {
      case 'platform':
        return 'purple';
      case 'reseller':
        return 'blue';
      case 'customer':
        return 'green';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Organization, b: Organization) => a.name.localeCompare(b.name),
      render: (name: string, record: Organization) => (
        <Link to={`/admin/organizations/${record.id}`}>{name}</Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Platform', value: 'platform' },
        { text: 'Reseller', value: 'reseller' },
        { text: 'Customer', value: 'customer' },
      ],
      onFilter: (value: any, record: Organization) => record.type === value,
      sorter: (a: Organization, b: Organization) => a.type.localeCompare(b.type),
      render: (type: string) => <Tag color={typeColor(type)}>{type}</Tag>,
    },
    {
      title: 'Parent',
      dataIndex: 'parent_org_id',
      key: 'parent_org_id',
      sorter: (a: Organization, b: Organization) => {
        const aParent = organizations.find(o => o.id === a.parent_org_id);
        const bParent = organizations.find(o => o.id === b.parent_org_id);
        return (aParent?.name || '').localeCompare(bParent?.name || '');
      },
      render: (id: number | null) => {
        if (!id) return '-';
        const parent = organizations.find(o => o.id === id);
        return parent ? parent.name : String(id);
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Organization) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          {record.type !== 'platform' && (
            <Popconfirm
              title="Delete organization"
              description="Are you sure you want to delete this organization?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <PageHeader
        title="Organizations"
        actions={
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Create Organization
            </Button>
            <PageHelp
              title="Organizations Help"
              sections={[
                {
                  title: 'What are Organizations?',
                  content:
                    'Organizations represent the hierarchy of your business: Platform (top level), Resellers (manage multiple customers), and Customers (own assets like yachts).\n\nEach organization can have users, assets, and SIM cards allocated to it.',
                },
                {
                  title: 'Creating an Organization',
                  content:
                    'Click "Create Organization" and choose a type:\n\n- Reseller: manages customer accounts, can allocate SIMs\n- Customer: owns yachts/assets, has crew members',
                  tips: [
                    'Resellers must have a platform or reseller as parent',
                    'Customers must have a reseller as parent',
                  ],
                },
                {
                  title: 'Organization Hierarchy',
                  content:
                    'Platform → Reseller → Customer\n\nA reseller can see all data from their customer organizations. Customers can only see their own data.',
                },
              ]}
            />
          </>
        }
      />

      <ResponsiveTableOrCards
        dataSource={organizations}
        rowKey={record => record.id}
        columns={columns}
        loading={loading}
        breakpoint="xl"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        renderCard={org => (
          <OrganizationListCard org={org} allOrgs={organizations} handlers={cardHandlers} />
        )}
        emptyText="No organizations found"
      />

      {/* Create Modal */}
      <ResponsiveModal
        title="Create Organization"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="Organization name" />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <LookupSelect category="org_type" placeholder="Select or enter type" />
          </Form.Item>
          <Form.Item name="parent_org_id" label="Parent Organization">
            <Select placeholder="Select parent (optional)" allowClear>
              {organizations.map(org => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name} ({org.type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create
              </Button>
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>

      {/* Edit Modal */}
      <ResponsiveModal
        title={`Edit Organization: ${editingOrg?.name || ''}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingOrg(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="Organization name" />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <LookupSelect
              category="org_type"
              placeholder="Select or enter type"
              disabled={editingOrg?.type === 'platform'}
            />
          </Form.Item>
          <Form.Item name="parent_org_id" label="Parent Organization">
            <Select
              placeholder="Select parent (optional)"
              allowClear
              disabled={editingOrg?.type === 'platform'}
            >
              {organizations
                .filter(o => o.id !== editingOrg?.id)
                .map(org => (
                  <Select.Option key={org.id} value={org.id}>
                    {org.name} ({org.type})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save
              </Button>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingOrg(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </div>
  );
};

export default OrganizationList;
